import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { ArrowRight, Calendar, Sparkles } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch { return ""; }
}

export default function Blog() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/blog/posts").then(({ data }) => {
            setPosts(data.posts || []);
        }).catch(() => { /* empty */ }).finally(() => setLoading(false));
    }, []);

    return (
        <div className="bg-cream min-h-screen" data-testid="blog-index">
            <Seo
                title="Journal"
                description="Stories from the cure room — Hawaiian botanicals, volcanic minerals, ASMR craft, and the slow work of making handmade soap."
            />
            <section className="max-w-6xl mx-auto px-5 md:px-10 pt-20 pb-12">
                <span className="divider-rule text-brown">
                    <Sparkles size={12} /> Island Gold Journal
                </span>
                <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl leading-[0.98] text-forest mt-6">
                    Stories from the
                    <br />
                    <span className="italic text-gold">cure room.</span>
                </h1>
                <p className="mt-6 max-w-xl text-brown leading-relaxed">
                    Hawaiian botanicals, volcanic minerals, ASMR craft, and the
                    slow work of making handmade soap by hand. New posts arrive
                    weekly while the first batch cures.
                </p>
            </section>

            <section className="max-w-6xl mx-auto px-5 md:px-10 pb-24">
                {loading && <p className="text-sm text-brown/50">Loading…</p>}
                {!loading && posts.length === 0 && (
                    <div className="rounded-sm border border-forest/15 bg-cream-warm px-6 py-10 text-center" data-testid="blog-empty">
                        <p className="font-serif text-2xl text-forest">The first entry is on its way.</p>
                        <p className="mt-2 text-sm text-brown">Check back soon — new stories from the studio are on the way.</p>
                    </div>
                )}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
                    {posts.map((p, i) => (
                        <Link
                            key={p.id}
                            to={`/blog/${p.slug}`}
                            className="group rounded-sm overflow-hidden border border-forest/15 bg-cream-warm hover:border-gold/60 transition-all rise-in"
                            style={{ animationDelay: `${i * 80}ms` }}
                            data-testid={`blog-card-${p.slug}`}
                        >
                            <div className="aspect-[16/9] overflow-hidden bg-forest/5">
                                {p.og_image_url ? (
                                    <img
                                        src={`${BACKEND}${p.og_image_url}`}
                                        alt={p.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gold/40">
                                        <Sparkles size={32} />
                                    </div>
                                )}
                            </div>
                            <div className="p-5">
                                <div className="flex items-center gap-2 text-[0.6rem] tracking-[0.3em] uppercase text-brown/60">
                                    <Calendar size={11} /> {formatDate(p.approved_at)}
                                </div>
                                <h2 className="font-serif text-2xl text-forest mt-2 leading-tight group-hover:text-gold transition-colors">
                                    {p.title}
                                </h2>
                                <p className="mt-2 text-sm text-brown leading-relaxed line-clamp-3">
                                    {p.excerpt}
                                </p>
                                <div className="mt-4 flex flex-wrap gap-1.5">
                                    {(p.suggested_tags || []).slice(0, 3).map((t) => (
                                        <span key={t} className="text-[0.6rem] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-gold/10 text-brown border border-gold/30">
                                            {t}
                                        </span>
                                    ))}
                                </div>
                                <div className="mt-5 inline-flex items-center gap-1.5 text-[0.65rem] tracking-[0.3em] uppercase text-gold font-semibold">
                                    Read <ArrowRight size={11} />
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}
